import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { dashboardAPI } from '../../services/api';
import Card from '../ui/Card';
import Spinner from '../ui/Spinner';
import Alert from '../ui/Alert';

// Cerc de progres pentru % conformitate. Verde ≥90, galben ≥70, roșu sub.
const ComplianceRing = ({ percent }) => {
  const { t } = useTranslation();
  const isNA = percent === null || percent === undefined;
  const value = isNA ? 0 : percent;
  const color = isNA
    ? 'text-slate-300 dark:text-slate-600'
    : value >= 90
      ? 'text-emerald-500'
      : value >= 70
        ? 'text-amber-500'
        : 'text-red-500';
  const r = 42;
  const circ = 2 * Math.PI * r;
  const offset = circ - (value / 100) * circ;

  return (
    <div className="relative w-28 h-28 flex-shrink-0">
      <svg className="w-28 h-28 -rotate-90" viewBox="0 0 100 100">
        <circle
          cx="50"
          cy="50"
          r={r}
          className="text-slate-100 dark:text-slate-800"
          stroke="currentColor"
          strokeWidth="8"
          fill="none"
        />
        {!isNA && (
          <circle
            cx="50"
            cy="50"
            r={r}
            className={color}
            stroke="currentColor"
            strokeWidth="8"
            fill="none"
            strokeDasharray={circ}
            strokeDashoffset={offset}
            strokeLinecap="round"
            style={{ transition: 'stroke-dashoffset 600ms ease' }}
          />
        )}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span
          className={`text-2xl font-bold tabular-nums ${isNA ? 'text-slate-400 dark:text-slate-500' : 'text-slate-900 dark:text-slate-100'}`}
        >
          {isNA ? '—' : `${value}%`}
        </span>
        <span className="text-[10px] text-slate-500 dark:text-slate-400">
          {t('execDashboard.compliance')}
        </span>
      </div>
    </div>
  );
};

// Bară mini-semafor pentru un rând de departament (proporții verde/galben/roșu).
const SemaforBar = ({ green, yellow, red, total }) => {
  if (!total) return <span className="text-slate-400 text-xs">—</span>;
  const pct = (n) => `${(n / total) * 100}%`;
  return (
    <div className="flex h-2.5 w-full rounded-full overflow-hidden bg-slate-100 dark:bg-slate-800">
      {green > 0 && <div className="bg-emerald-500" style={{ width: pct(green) }} />}
      {yellow > 0 && <div className="bg-amber-500" style={{ width: pct(yellow) }} />}
      {red > 0 && <div className="bg-red-500" style={{ width: pct(red) }} />}
    </div>
  );
};

// Donut chart pentru conformitate fișe: verde = complet semnate, galben = parțial, roșu = nesemnate.
// SVG manual (fără librărie de chart). Total = fully + partial + unsigned. Dacă total=0, afișează „—".
const SheetDonut = ({ fully, partial, unsigned }) => {
  const { t } = useTranslation();
  const total = (fully || 0) + (partial || 0) + (unsigned || 0);
  if (total === 0) return null;
  const r = 38;
  const circ = 2 * Math.PI * r;
  const pctFully = fully / total;
  const pctPartial = partial / total;
  // Construim 3 arcuri concatenate folosind strokeDasharray + strokeDashoffset.
  const fullyLen = pctFully * circ;
  const partialLen = pctPartial * circ;
  const unsignedLen = circ - fullyLen - partialLen;
  return (
    <div className="relative w-24 h-24 flex-shrink-0">
      <svg className="w-24 h-24 -rotate-90" viewBox="0 0 100 100">
        {/* Background gray */}
        <circle
          cx="50"
          cy="50"
          r={r}
          className="text-slate-100 dark:text-slate-800"
          stroke="currentColor"
          strokeWidth="10"
          fill="none"
        />
        {/* Verde — primul arc, de la 0 */}
        {fully > 0 && (
          <circle
            cx="50"
            cy="50"
            r={r}
            className="text-emerald-500"
            stroke="currentColor"
            strokeWidth="10"
            fill="none"
            strokeDasharray={`${fullyLen} ${circ - fullyLen}`}
            strokeDashoffset={0}
          />
        )}
        {/* Galben — al doilea arc, începe după verde */}
        {partial > 0 && (
          <circle
            cx="50"
            cy="50"
            r={r}
            className="text-amber-500"
            stroke="currentColor"
            strokeWidth="10"
            fill="none"
            strokeDasharray={`${partialLen} ${circ - partialLen}`}
            strokeDashoffset={-fullyLen}
          />
        )}
        {/* Roșu — al treilea arc */}
        {unsigned > 0 && (
          <circle
            cx="50"
            cy="50"
            r={r}
            className="text-red-500"
            stroke="currentColor"
            strokeWidth="10"
            fill="none"
            strokeDasharray={`${unsignedLen} ${circ - unsignedLen}`}
            strokeDashoffset={-(fullyLen + partialLen)}
          />
        )}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-lg font-bold tabular-nums text-slate-900 dark:text-slate-100">
          {total}
        </span>
        <span className="text-[9px] text-slate-500 dark:text-slate-400">
          {t('execDashboard.sheetsUnit')}
        </span>
      </div>
    </div>
  );
};

// Mini-legend pentru donut (3 chip-uri cu count colorat).
const DonutLegend = ({ fully, partial, unsigned }) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-1.5 text-xs">
      <div className="flex items-center gap-2">
        <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
        <span className="text-slate-700 dark:text-slate-200">
          <strong className="tabular-nums">{fully}</strong> {t('execDashboard.fullySigned')}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <span className="w-2.5 h-2.5 rounded-full bg-amber-500" />
        <span className="text-slate-700 dark:text-slate-200">
          <strong className="tabular-nums">{partial}</strong> {t('execDashboard.partial')}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <span className="w-2.5 h-2.5 rounded-full bg-red-500" />
        <span className="text-slate-700 dark:text-slate-200">
          <strong className="tabular-nums">{unsigned}</strong> {t('execDashboard.unsigned')}
        </span>
      </div>
    </div>
  );
};

// Etichetă scurtă pentru tipul cursului (badge inline).
const courseTypeLabel = (t) => (t || '').replace('_', ' ');

const KpiTile = ({ label, value, accent, sublabel }) => {
  const map = {
    green:
      'text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800/60',
    amber:
      'text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800/60',
    red: 'text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800/60',
    slate:
      'text-slate-700 dark:text-slate-300 bg-slate-50 dark:bg-slate-800/60 border-slate-200 dark:border-slate-700',
  };
  return (
    <div className={`rounded-xl border p-4 ${map[accent] || map.slate}`}>
      <p className="text-xs font-medium opacity-80">{label}</p>
      <p className="mt-1 text-2xl font-bold tabular-nums">{value}</p>
      {sublabel && <p className="text-[11px] opacity-70 mt-0.5">{sublabel}</p>}
    </div>
  );
};

// Prop `companyId` opțional — folosit de SSM-iști când deschid dashboard-ul executiv al unei
// firme client din portofoliu. Fără prop, endpoint-ul returnează firma proprie a userului
// (comportament standard pentru COMPANY_ADMIN / DIRECTOR_GENERAL).
const ExecutiveDashboard = ({ companyId = null }) => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await dashboardAPI.getExecutive(companyId);
        if (!cancelled) setData(res.data);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.error || err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  if (loading) return <Spinner size="lg" label={t('execDashboard.loading')} />;
  if (error) return <Alert type="error">{error}</Alert>;
  if (!data) return null;

  const { kpi, byDepartment, sheetCompliance, pendingByEmployee } = data;

  return (
    <div className="space-y-6" data-onboarding="exec-dashboard">
      {/* Sumar conformitate firmă */}
      <Card>
        <div className="flex flex-col sm:flex-row items-center gap-6">
          <ComplianceRing percent={kpi.compliancePercent} />
          <div className="flex-1 w-full">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-1">
              {t('execDashboard.firmCompliance', { company: data.company?.name })}
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-3">
              {kpi.ipTotal > 0
                ? t('execDashboard.complianceDesc', { up: kpi.ipUpToDate, total: kpi.ipTotal })
                : t('execDashboard.noIPConfigured')}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <KpiTile
                accent="slate"
                label={t('execDashboard.totalEmployees')}
                value={kpi.totalEmployees}
              />
              <KpiTile
                accent="amber"
                label={t('execDashboard.ipExpiringSoon')}
                value={kpi.ipExpiringSoon}
                sublabel={t('execDashboard.withinDays', { days: data.thresholds?.yellowDays })}
              />
              <KpiTile
                accent="red"
                label={t('execDashboard.ipExpired')}
                value={kpi.ipExpired}
                sublabel={t('execDashboard.urgent')}
              />
              <KpiTile
                accent="slate"
                label={t('execDashboard.pendingSheets')}
                value={kpi.pendingSheets}
                sublabel={t('execDashboard.awaitingSignatures')}
              />
            </div>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-3 pt-4 border-t border-slate-100 dark:border-slate-800">
          <Link
            to="/dashboard-ip"
            className="text-sm text-primary-600 dark:text-primary-300 hover:underline font-medium"
          >
            {t('execDashboard.viewTrafficLight')} →
          </Link>
          <Link
            to="/issues"
            className="text-sm text-primary-600 dark:text-primary-300 hover:underline font-medium"
          >
            {t('execDashboard.viewIssues')} →
          </Link>
          <Link
            to="/employees"
            className="text-sm text-primary-600 dark:text-primary-300 hover:underline font-medium"
          >
            {t('execDashboard.viewEmployees')} →
          </Link>
        </div>
      </Card>

      {/* Conformitate FIȘE — secțiune nouă cu donut chart + status detaliat. O fișă e
          „complet semnată" doar când TOATE slot-urile din matrice (EMPLOYEE + CLM + Admin/SSM/PSI
          în funcție de tip) au cel puțin o semnătură. */}
      {sheetCompliance && sheetCompliance.total > 0 && (
        <Card>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
            <SheetDonut
              fully={sheetCompliance.fullySigned}
              partial={sheetCompliance.partial}
              unsigned={sheetCompliance.unsigned}
            />
            <div className="flex-1 w-full">
              <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
                <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                  {t('execDashboard.sheetComplianceTitle')}
                </h3>
                <span
                  className={`text-lg font-bold tabular-nums ${
                    sheetCompliance.compliancePercent >= 90
                      ? 'text-emerald-600 dark:text-emerald-300'
                      : sheetCompliance.compliancePercent >= 70
                        ? 'text-amber-600 dark:text-amber-300'
                        : 'text-red-600 dark:text-red-300'
                  }`}
                >
                  {sheetCompliance.compliancePercent}%
                </span>
              </div>
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-3">
                {t('execDashboard.sheetComplianceDesc')}
              </p>
              <DonutLegend
                fully={sheetCompliance.fullySigned}
                partial={sheetCompliance.partial}
                unsigned={sheetCompliance.unsigned}
              />
            </div>
          </div>
        </Card>
      )}

      {/* Listă „Cine mai are de făcut" — angajații cu fișe incomplete și slot-urile lipsă. */}
      {pendingByEmployee && pendingByEmployee.length > 0 && (
        <Card>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
              {t('execDashboard.whoNeedsToSign', { count: pendingByEmployee.length })}
            </h3>
            <span className="text-xs text-slate-500 dark:text-slate-400">
              {t('execDashboard.sortedByIncomplete')}
            </span>
          </div>
          <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
            {pendingByEmployee.map((emp) => (
              <div
                key={emp.employeeId}
                className="rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50/40 dark:bg-slate-900/40 p-3"
              >
                <div className="flex items-baseline justify-between gap-3 mb-1.5">
                  <p className="font-semibold text-slate-900 dark:text-slate-100 text-sm">
                    {emp.employeeName}
                    {emp.departmentName && (
                      <span className="ml-2 text-xs text-slate-500 dark:text-slate-400 font-normal">
                        · {emp.departmentName}
                      </span>
                    )}
                  </p>
                  <span className="text-xs text-warning-700 dark:text-warning-300 font-medium flex-shrink-0">
                    {t('execDashboard.incompleteSheets', { count: emp.missingSheets.length })}
                  </span>
                </div>
                <ul className="space-y-1">
                  {emp.missingSheets.map((sh) => {
                    const isSU = (sh.courseType || '').endsWith('_SU');
                    return (
                      <li key={sh.trainingId} className="flex flex-wrap items-center gap-2 text-xs">
                        <span
                          className={`text-[10px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wide ${isSU ? 'bg-danger-100 dark:bg-danger-950/50 text-danger-700 dark:text-danger-300' : 'bg-primary-100 dark:bg-primary-950/50 text-primary-700 dark:text-primary-300'}`}
                        >
                          {courseTypeLabel(sh.courseType)}
                        </span>
                        <span className="text-slate-700 dark:text-slate-200">{sh.courseTitle}</span>
                        <span className="text-slate-400 dark:text-slate-500">·</span>
                        <span className="text-slate-500 dark:text-slate-400">
                          {t('execDashboard.missingLabel')}
                        </span>
                        {sh.missingSlots.map((slot) => (
                          <span
                            key={slot.key}
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-warning-50 dark:bg-warning-950/30 text-warning-700 dark:text-warning-300 text-[10px] font-medium border border-warning-200 dark:border-warning-800"
                          >
                            {slot.label}
                          </span>
                        ))}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Conformitate per departament */}
      <Card>
        <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100 mb-3">
          {t('execDashboard.byDepartment')}
        </h3>
        {byDepartment.length === 0 ? (
          <p className="text-sm text-slate-500 dark:text-slate-400 py-4 text-center">
            {t('execDashboard.noDeptData')}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-800">
                  <th className="py-2 pr-4 font-medium">{t('execDashboard.colDept')}</th>
                  <th className="py-2 px-3 font-medium text-center w-20">
                    {t('execDashboard.colCompliance')}
                  </th>
                  <th className="py-2 px-3 font-medium w-40">{t('execDashboard.colStatus')}</th>
                  <th className="py-2 px-3 font-medium text-center text-emerald-700 dark:text-emerald-300">
                    {t('execDashboard.colOnTime')}
                  </th>
                  <th className="py-2 px-3 font-medium text-center text-amber-700 dark:text-amber-300">
                    {t('execDashboard.colSoon')}
                  </th>
                  <th className="py-2 px-3 font-medium text-center text-red-700 dark:text-red-300">
                    {t('execDashboard.colExpired')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {byDepartment.map((d) => (
                  <tr
                    key={d.departmentId || 'none'}
                    className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800"
                  >
                    <td className="py-2.5 pr-4 font-medium text-slate-900 dark:text-slate-100">
                      {d.departmentName || t('execDashboard.noDept')}
                    </td>
                    <td className="py-2.5 px-3 text-center tabular-nums font-semibold">
                      {d.compliancePercent === null ? '—' : `${d.compliancePercent}%`}
                    </td>
                    <td className="py-2.5 px-3">
                      <SemaforBar green={d.green} yellow={d.yellow} red={d.red} total={d.total} />
                    </td>
                    <td className="py-2.5 px-3 text-center tabular-nums text-emerald-700 dark:text-emerald-300">
                      {d.green}
                    </td>
                    <td className="py-2.5 px-3 text-center tabular-nums text-amber-700 dark:text-amber-300">
                      {d.yellow}
                    </td>
                    <td className="py-2.5 px-3 text-center tabular-nums text-red-700 dark:text-red-300 font-semibold">
                      {d.red}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

export default ExecutiveDashboard;
